import { useState, type ReactNode } from 'react'

import type { AvailableDate } from '../../api/appointments'
import { amtcDialNumber, amtcDisplayNumber } from '../../lib/amtc'
import { Icon } from '../Icon'

/** Format an ISO date (YYYY-MM-DD) as e.g. "Tue 21 Jul" without timezone drift. */
function labelForDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number)
  const dt = new Date(y!, (m ?? 1) - 1, d) // local, no TZ shift
  return dt.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  })
}

/**
 * Date picker for an appointment request (M14-T08, ADR-0030). Dates come from the
 * server already filtered to the session's weekday and the two-week window, tagged
 * by week. We reveal week 1 first; "Show next week" reveals week 2; after that the
 * AMTC call-centre fallback appears. The fallback is ALSO the whole content when
 * there are no in-window dates — a supported outcome, not an error.
 */
export function DatePicker({
  dates,
  selected,
  onSelect,
}: {
  dates: AvailableDate[]
  selected: string | null
  onSelect: (iso: string) => void
}): ReactNode {
  const [weeksShown, setWeeksShown] = useState(1)
  const maxWeek = dates.reduce((m, d) => Math.max(m, d.week), 0)
  const visible = dates.filter((d) => d.week <= weeksShown)

  if (dates.length === 0) {
    return <AmtcFallback />
  }

  return (
    <fieldset className="flex flex-col gap-2 border-0 p-0">
      <legend className="mb-1 text-[13px] font-medium text-ink-1">
        Pick a date
      </legend>
      <div className="flex flex-wrap gap-2">
        {visible.map((d) => {
          const checked = d.date === selected
          return (
            <label
              key={d.date}
              className={`cursor-pointer rounded-full border px-3 py-1.5 text-[13px] ${
                checked
                  ? 'border-accent bg-accent text-accent-on'
                  : 'border-line-2 bg-bg-app text-ink-1 hover:bg-bg-soft'
              }`}
            >
              <input
                type="radio"
                name="appointment-date"
                value={d.date}
                checked={checked}
                onChange={() => onSelect(d.date)}
                className="sr-only"
              />
              {labelForDate(d.date)}
            </label>
          )
        })}
      </div>

      {weeksShown < maxWeek ? (
        <button
          type="button"
          onClick={() => setWeeksShown((w) => w + 1)}
          className="mt-1 w-fit text-[13px] font-medium text-accent-ink hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          Show next week
        </button>
      ) : (
        <div className="mt-2">
          <AmtcFallback compact />
        </div>
      )}
    </fieldset>
  )
}

/** The AMTC call-centre hand-off — a supported outcome (a later date, or no
 * in-window session), styled as a helpful next step, not an error. */
export function AmtcFallback({ compact = false }: { compact?: boolean }): ReactNode {
  const dial = amtcDialNumber()
  const shown = amtcDisplayNumber()
  return (
    <div
      className={`rounded-r3 border border-line-1 bg-bg-soft p-3 ${
        compact ? '' : 'text-center'
      }`}
    >
      <p className="text-[13px] text-ink-2">
        Need a later date? Call the AMTC team and we&apos;ll arrange it.
      </p>
      {/* Unguarded until 2026-08-31: this rendered `env.amtcPhone` straight into
          a `tel:` while that value was still a placeholder, so the button
          dialled nothing. `amtcDialNumber` checks the value, not the shape. */}
      {dial !== null && shown !== null && (
        <a
          href={`tel:${dial}`}
          className="mt-2 inline-flex items-center gap-1.5 rounded-r2 border border-accent bg-accent-bg px-3 py-2 text-[13px] font-medium text-accent-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          <Icon name="phone" size={14} /> {shown}
        </a>
      )}
    </div>
  )
}
